import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  NavigateNext,
  NavigateBefore,
  ContactPage,
  Home,
  Assessment,
  Star
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { useScore } from '../context/ScoreContext';
import ProgressBar from '../components/Gamification/ProgressBar';
import StarRating from '../components/Gamification/StarRating';

// Critical Level 1 Tenancy Compliance Questions - ALL must pass for 1 star minimum
const SAMPLE_QUESTIONS = [
  {
    id: 'epc_rating',
    category: 'Critical Tenancy Compliance',
    level: 1,
    text: 'Does your property have an EPC that is E or better?',
    type: 'boolean' as const,
    weight: -100, // Negative weight = critical failure if "No"
    required: true,
    critical: true
  },
  {
    id: 'gas_safety',
    category: 'Critical Tenancy Compliance',
    level: 1,
    text: 'Do you have a valid Gas Safety Certificate (if applicable)?',
    type: 'multiple' as const,
    options: ['Yes - valid certificate', 'No - certificate required', 'No gas in this property'],
    weight: -100, // Negative weight if "No - certificate required"
    required: true,
    critical: true
  },
  {
    id: 'electrical_safety',
    category: 'Critical Tenancy Compliance',
    level: 1,
    text: 'Do you have a valid Electrical Installation Condition Report (EICR)?',
    type: 'boolean' as const,
    weight: -100, // Negative weight = critical failure if "No"
    required: true,
    critical: true
  },
  {
    id: 'fire_safety_compliance',
    category: 'Critical Tenancy Compliance',
    level: 1,
    text: 'Are working smoke alarms installed on every floor?',
    type: 'boolean' as const,
    weight: -100, // Negative weight = critical failure if "No"
    required: true,
    critical: true
  },
  {
    id: 'hmo_license',
    category: 'Critical Tenancy Compliance',
    level: 1,
    text: 'If this is a House in Multiple Occupation (HMO), do you have the required license?',
    type: 'multiple' as const,
    options: ['Yes - licensed HMO', 'No - not licensed', 'Not applicable - not an HMO'],
    weight: -100, // Negative weight if "No - not licensed"
    required: true,
    critical: true
  },
  {
    id: 'deposit_protection',
    category: 'Critical Tenancy Compliance',
    level: 1,
    text: 'Are tenant deposits protected in an approved scheme?',
    type: 'boolean' as const,
    weight: -100, // Negative weight = critical failure if "No"
    required: true,
    critical: true
  }
];

const AssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch: userDispatch } = useUser();
  const { state: scoreState, dispatch: scoreDispatch } = useScore();

  const [currentStep, setCurrentStep] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [contactForm, setContactForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'owner' as 'owner' | 'occupier' | 'agent'
  });
  const [propertyForm, setPropertyForm] = useState({
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA'
    },
    propertyType: 'residential' as 'residential' | 'commercial' | 'industrial' | 'mixed',
    yearBuilt: '',
    size: ''
  });
  const [currentAnswer, setCurrentAnswer] = useState<string | boolean>('');

  const steps = ['Contact Information', 'Property Details', 'Assessment Questions'];
  const availableQuestions = SAMPLE_QUESTIONS.filter(q =>
    scoreState.unlockedLevels.includes(q.level)
  );

  const currentQuestion = availableQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex >= availableQuestions.length - 1;
  const assessmentProgress = ((currentQuestionIndex + 1) / availableQuestions.length) * 100;

  useEffect(() => {
    // Auto-calculate score when answers change
    scoreDispatch({ type: 'CALCULATE_SCORE' });
  }, [scoreState.answers, scoreDispatch]);

  const handleContactSubmit = () => {
    userDispatch({ type: 'SET_CONTACT', payload: contactForm });
    setCurrentStep(1);
  };

  const handlePropertySubmit = () => {
    const propertyData = {
      ...propertyForm,
      yearBuilt: propertyForm.yearBuilt ? parseInt(propertyForm.yearBuilt) : undefined,
      size: propertyForm.size ? parseFloat(propertyForm.size) : undefined
    };
    userDispatch({ type: 'SET_PROPERTY', payload: propertyData });
    setCurrentStep(2);
  };

  const handleAnswerSubmit = () => {
    if (currentAnswer !== '') {
      scoreDispatch({
        type: 'ANSWER_QUESTION',
        payload: {
          questionId: currentQuestion.id,
          value: currentAnswer,
          timestamp: new Date()
        }
      });

      // Check if we should unlock the next level
      const currentLevelQuestions = availableQuestions.filter(q => q.level === scoreState.currentLevel);
      const answeredCurrentLevel = scoreState.answers.filter(a =>
        currentLevelQuestions.some(q => q.id === a.questionId)
      );

      if (answeredCurrentLevel.length >= currentLevelQuestions.length - 1) {
        scoreDispatch({ type: 'UNLOCK_LEVEL', payload: scoreState.currentLevel + 1 });
      }

      if (isLastQuestion) {
        navigate('/results');
      } else {
        setCurrentQuestionIndex(prev => prev + 1);
        setCurrentAnswer('');
      }
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      // Load previous answer if exists
      const previousAnswer = scoreState.answers.find(a => a.questionId === availableQuestions[currentQuestionIndex - 1].id);
      const previousValue = previousAnswer?.value;

      // Ensure the value matches the expected type
      if (typeof previousValue === 'boolean' || typeof previousValue === 'string') {
        setCurrentAnswer(previousValue);
      } else {
        setCurrentAnswer('');
      }
    }
  };

  const renderContactForm = () => (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ContactPage sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
              Contact Information
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={contactForm.firstName}
                onChange={(e) => setContactForm(prev => ({ ...prev, firstName: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={contactForm.lastName}
                onChange={(e) => setContactForm(prev => ({ ...prev, lastName: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={contactForm.phone}
                onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl>
                <FormLabel>I am the property:</FormLabel>
                <RadioGroup
                  row
                  value={contactForm.role}
                  onChange={(e) => setContactForm(prev => ({ ...prev, role: e.target.value as any }))}
                >
                  <FormControlLabel value="owner" control={<Radio />} label="Owner" />
                  <FormControlLabel value="occupier" control={<Radio />} label="Occupier" />
                  <FormControlLabel value="agent" control={<Radio />} label="Agent" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, textAlign: 'right' }}>
            <Button
              variant="contained"
              onClick={handleContactSubmit}
              disabled={!contactForm.firstName || !contactForm.lastName || !contactForm.email || !contactForm.phone}
              endIcon={<NavigateNext />}
            >
              Continue
            </Button>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  const renderPropertyForm = () => (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Home sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
              Property Details
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={propertyForm.address.street}
                onChange={(e) => setPropertyForm(prev => ({
                  ...prev,
                  address: { ...prev.address, street: e.target.value }
                }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={propertyForm.address.city}
                onChange={(e) => setPropertyForm(prev => ({
                  ...prev,
                  address: { ...prev.address, city: e.target.value }
                }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="State"
                value={propertyForm.address.state}
                onChange={(e) => setPropertyForm(prev => ({
                  ...prev,
                  address: { ...prev.address, state: e.target.value }
                }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={propertyForm.address.zipCode}
                onChange={(e) => setPropertyForm(prev => ({
                  ...prev,
                  address: { ...prev.address, zipCode: e.target.value }
                }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <FormLabel>Property Type</FormLabel>
                <RadioGroup
                  value={propertyForm.propertyType}
                  onChange={(e) => setPropertyForm(prev => ({ ...prev, propertyType: e.target.value as any }))}
                >
                  <FormControlLabel value="residential" control={<Radio />} label="Residential" />
                  <FormControlLabel value="commercial" control={<Radio />} label="Commercial" />
                  <FormControlLabel value="industrial" control={<Radio />} label="Industrial" />
                  <FormControlLabel value="mixed" control={<Radio />} label="Mixed Use" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Year Built"
                type="number"
                value={propertyForm.yearBuilt}
                onChange={(e) => setPropertyForm(prev => ({ ...prev, yearBuilt: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Size (sq ft)"
                type="number"
                value={propertyForm.size}
                onChange={(e) => setPropertyForm(prev => ({ ...prev, size: e.target.value }))}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={() => setCurrentStep(0)}
              startIcon={<NavigateBefore />}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handlePropertySubmit}
              disabled={!propertyForm.address.street || !propertyForm.address.city || !propertyForm.address.state || !propertyForm.address.zipCode}
              endIcon={<NavigateNext />}
            >
              Start Assessment
            </Button>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  const renderAssessmentQuestion = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      {/* Progress Bar */}
      <Box sx={{ mb: 4 }}>
        <ProgressBar
          currentLevel={scoreState.currentLevel}
          maxLevel={5}
          progress={assessmentProgress}
          levelName={scoreState.complianceLevel?.name || 'Starting Level'}
        />
      </Box>

      {/* Current Score Display */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Current Score</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {scoreState.totalScore}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Rating</Typography>
              <StarRating rating={scoreState.starRating} size="large" />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Question Card */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Assessment sx={{ mr: 2, color: 'primary.main' }} />
            <Box>
              <Chip
                label={currentQuestion.category}
                color="primary"
                size="small"
                sx={{ mr: 2 }}
              />
              <Chip
                label={`Level ${currentQuestion.level}`}
                color="secondary"
                size="small"
              />
            </Box>
          </Box>

          <Typography variant="h5" component="h3" sx={{ mb: 4, fontWeight: 'medium' }}>
            {currentQuestion.text}
          </Typography>

          {currentQuestion.type === 'boolean' && (
            <FormControl>
              <RadioGroup
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(e.target.value === 'true')}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Yes"
                  sx={{ mb: 1 }}
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
            </FormControl>
          )}

          {currentQuestion.type === 'multiple' && currentQuestion.options && (
            <FormControl>
              <RadioGroup
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(e.target.value)}
              >
                {currentQuestion.options.map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio />}
                    label={option}
                    sx={{ mb: 1 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              startIcon={<NavigateBefore />}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              onClick={handleAnswerSubmit}
              disabled={currentAnswer === ''}
              endIcon={isLastQuestion ? <Star /> : <NavigateNext />}
              sx={{
                background: isLastQuestion ? 'linear-gradient(45deg, #f57c00, #ff9800)' : undefined
              }}
            >
              {isLastQuestion ? 'Complete Assessment' : 'Next Question'}
            </Button>
          </Box>

          {/* Progress indicator */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Question {currentQuestionIndex + 1} of {availableQuestions.length}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={assessmentProgress}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Box>
      {/* Stepper */}
      <Box sx={{ mb: 4 }}>
        <Stepper activeStep={currentStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Content */}
      <AnimatePresence mode="wait">
        {currentStep === 0 && renderContactForm()}
        {currentStep === 1 && renderPropertyForm()}
        {currentStep === 2 && renderAssessmentQuestion()}
      </AnimatePresence>
    </Box>
  );
};

export default AssessmentPage;